import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  Popover,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import SearchIcon from '@mui/icons-material/Search';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';

import { useMytaverseEvent } from '@core/dashboard/providers';
import BrandingsService, {
  IBrandingApi,
  IColorScheme,
} from '@services/BrandingsService';
import { usePreventGameKeydownListening } from '@hooks';

import { useEditorMode } from '../EditorModeContext';
import { useBrandingSwitch } from '../useBrandingSwitch';
import { useBrandingBarStyles } from './styles';

interface QuickSwitcherProps {
  anchorEl: HTMLElement | null;
  onCreateNew?: () => void;
  onOpenLibrary?: () => void;
}

const renderSwatch = (
  colors: IColorScheme | undefined,
  classes: ReturnType<typeof useBrandingBarStyles>,
) => {
  const has =
    colors?.primaryColor || colors?.secondaryColor || colors?.outlineColor;
  if (!has) {
    return (
      <Box sx={classes.brandSwatchRow}>
        <Box sx={classes.brandSwatchPlaceholder} />
      </Box>
    );
  }
  return (
    <Box sx={classes.brandSwatchRow}>
      <Box
        sx={[
          classes.brandSwatchStripe,
          {
            backgroundColor: colors?.primaryColor || 'rgba(255,255,255,0.1)',
          },
        ]}
      />
      <Box
        sx={[
          classes.brandSwatchStripe,
          {
            backgroundColor: colors?.secondaryColor || 'rgba(255,255,255,0.1)',
          },
        ]}
      />
      <Box
        sx={[
          classes.brandSwatchStripe,
          {
            backgroundColor: colors?.outlineColor || 'rgba(255,255,255,0.1)',
          },
        ]}
      />
    </Box>
  );
};

/**
 * Renders the configuration dropdown under the BrandingBar pill: a searchable
 * list where selecting a row activates that configuration, plus footer
 * shortcuts to the create flow and the full library modal.
 *
 * @param props.anchorEl - BrandingBar element the popover attaches to; `null` keeps it detached.
 * @param props.onCreateNew - Called when the create shortcut is clicked.
 * @param props.onOpenLibrary - Called when the library shortcut is clicked.
 */
const QuickSwitcher = ({
  anchorEl,
  onCreateNew,
  onOpenLibrary,
}: QuickSwitcherProps) => {
  const { t: translate } = useTranslation();
  const classes = useBrandingBarStyles();

  const { isQuickSwitcherOpen, closeQuickSwitcher, setAutosaveStatus } =
    useEditorMode();
  const { currentRoom, currentBrandingId } = useMytaverseEvent();

  const mapId = currentRoom?.map?.mapId;
  const activeBrandingId = currentRoom?.activeBrandingId;

  const searchInputRef = usePreventGameKeydownListening<HTMLInputElement>();

  const [search, setSearch] = useState('');
  const [brandings, setBrandings] = useState<IBrandingApi[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { switchBranding, isSwitching } = useBrandingSwitch({
    onStatus: setAutosaveStatus,
    onSwitched: closeQuickSwitcher,
  });

  useEffect(() => {
    if (!isQuickSwitcherOpen || !mapId) {
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    BrandingsService.getBrandings({ mapId, limit: 100 })
      .then((res) => {
        if (!cancelled) {
          setBrandings(res.brandings ?? []);
        }
      })
      .catch((loadError) => {
        console.error(
          `Failed to load brandings for quick switcher: ${
            (loadError as Error)?.message
          }`,
          { mapId, brandingId: currentBrandingId },
        );
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [isQuickSwitcherOpen, mapId, currentBrandingId]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) {
      return brandings;
    }
    return brandings.filter((b) =>
      (b.name || 'Unnamed').toLowerCase().includes(q),
    );
  }, [brandings, search]);

  const handleSelectAndActivate = (brandingId: string) =>
    switchBranding(brandingId, brandings);

  return (
    <Popover
      open={isQuickSwitcherOpen}
      anchorEl={anchorEl}
      onClose={closeQuickSwitcher}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      PaperProps={{ sx: classes.quickSwitcherPaper }}
    >
      <Box sx={classes.quickSearchWrap}>
        <TextField
          inputRef={searchInputRef}
          fullWidth
          size="small"
          placeholder={translate('editorMode.quickSearchPlaceholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => {
            if (e.key !== 'Escape') {
              e.stopPropagation();
            }
          }}
          onKeyUp={(e) => {
            if (e.key !== 'Escape') {
              e.stopPropagation();
            }
          }}
          sx={classes.quickSearchField}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          inputProps={{ 'data-testid': 'quick-switcher-search-input' }}
        />
      </Box>

      <Box sx={classes.quickList}>
        {isLoading || isSwitching ? (
          <Box sx={classes.quickLoader}>
            <CircularProgress size={18} />
          </Box>
        ) : filtered.length === 0 ? (
          <Typography sx={classes.quickEmpty}>
            {translate('editorMode.quickNoResults')}
          </Typography>
        ) : (
          filtered.map((b) => {
            const id = b.brandingId;
            const isEditing = id === currentBrandingId;
            const isLive = !!activeBrandingId && id === activeBrandingId;
            return (
              <Tooltip
                key={id}
                title={
                  isLive
                    ? translate('editorMode.liveTooltip')
                    : translate('editorMode.activateOnSelect')
                }
                placement="left"
                enterDelay={400}
              >
                <Box
                  sx={[classes.quickRow, isEditing && classes.quickRowActive]}
                  onClick={() => !isLive && handleSelectAndActivate(id)}
                  data-testid={`quick-switcher-row-${id}`}
                >
                  {renderSwatch(b.brandingColors, classes)}
                  <Typography sx={classes.quickName}>
                    {b.name || translate('adminToolbox.unnamed')}
                  </Typography>
                  <Box sx={classes.quickStatusSlot}>
                    {isLive ? (
                      <RadioButtonCheckedIcon
                        sx={classes.quickStatusLiveIcon}
                      />
                    ) : (
                      <RadioButtonUncheckedIcon
                        sx={classes.quickStatusInactiveIcon}
                      />
                    )}
                  </Box>
                </Box>
              </Tooltip>
            );
          })
        )}
      </Box>

      <Box sx={classes.quickFooter}>
        <Button
          variant="outlined"
          sx={classes.quickFooterButton}
          onClick={() => {
            closeQuickSwitcher();
            onCreateNew?.();
          }}
          data-testid="quick-switcher-create-btn"
        >
          {translate('editorMode.quickCreate')}
        </Button>
        <Button
          variant="outlined"
          sx={classes.quickFooterButton}
          onClick={() => {
            closeQuickSwitcher();
            onOpenLibrary?.();
          }}
          data-testid="quick-switcher-library-btn"
        >
          {translate('editorMode.quickAllBrandings')}
        </Button>
      </Box>
    </Popover>
  );
};

export default QuickSwitcher;
